/**
 * Validazione del codice fiscale italiano.
 *
 * Il controllo di forma (6 lettere, 2 cifre, ...) non basta: accetta qualsiasi
 * refuso che rispetti lo schema, comprese date di nascita inesistenti come il
 * giorno 39. Il 16° carattere è invece calcolato dai primi 15 e permette di
 * intercettare l'errore di battitura di una singola cifra.
 *
 * Nota sull'omocodia: nei CF omocodici alcune cifre sono sostituite da lettere,
 * quindi non superano CF_PATTERN. Per quei casi resta il percorso già previsto
 * in pagina ("Procedi comunque" con inserimento manuale di data e luogo).
 */

const CF_PATTERN = /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/

const ALFABETO = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'

/**
 * Valori di conversione per i caratteri in posizione dispari (1°, 3°, 5°, ...),
 * indicizzati come ALFABETO. Le posizioni pari usano invece il valore
 * "naturale" del carattere: 0-9 per le cifre, 0-25 per le lettere.
 */
const VALORI_DISPARI = [
  1, 0, 5, 7, 9, 13, 15, 17, 19, 21,
  1, 0, 5, 7, 9, 13, 15, 17, 19, 21, 2, 4, 18, 20, 11,
  3, 6, 8, 12, 14, 16, 10, 22, 25, 24, 23,
]

function normalizza(cf: string): string {
  return cf.trim().toUpperCase()
}

export function hasFormatoCodiceFiscaleValido(cf: string): boolean {
  return CF_PATTERN.test(normalizza(cf))
}

/**
 * Calcola il carattere di controllo atteso dai primi 15 caratteri del CF.
 * Restituisce null se l'input è troppo corto o contiene caratteri non ammessi.
 */
export function calcolaCarattereControllo(cf: string): string | null {
  const normalizzato = normalizza(cf)
  if (normalizzato.length < 15) return null

  let somma = 0
  for (let i = 0; i < 15; i++) {
    const posizione = ALFABETO.indexOf(normalizzato[i])
    if (posizione === -1) return null

    const valorePari = posizione < 10 ? posizione : posizione - 10
    // L'indice 0-based pari corrisponde alla posizione dispari del CF.
    somma += i % 2 === 0 ? VALORI_DISPARI[posizione] : valorePari
  }

  return String.fromCharCode(65 + (somma % 26))
}

/**
 * Verifica formato e carattere di controllo. Un CF realmente emesso li supera
 * sempre entrambi: se questo controllo fallisce il codice è stato trascritto male.
 */
export function isCodiceFiscaleValido(cf: string): boolean {
  const normalizzato = normalizza(cf)
  if (!hasFormatoCodiceFiscaleValido(normalizzato)) return false
  return calcolaCarattereControllo(normalizzato) === normalizzato[15]
}
